import React from 'react';
import {connect} from 'react-redux';
import compose from 'recompose/compose';
import withHandlers from 'recompose/withHandlers';
import isEmpty from 'lodash/isEmpty';
import reject from 'lodash/reject';
import find from 'lodash/find';
import includes from 'lodash/includes';
import {
    extractResourceIdsWithPath,
    getBreakResource,
    isNoU,
    isTruthy,
    isUndefined,
    omit
} from "../utils/DBUtils";
import {saveResourceFieldAction, saveResourceFieldErrorAction} from "../resources/actions";
import isFunction from "lodash/isFunction";
import blockParentUpdates from "./blockParentUpdates";
import dotProp from "dot-prop";
import catchErrors from "./catchErrors";
import {getUserResourcePermissions} from "../pages/Profile/selectors";

/**
 * Shared code to extract errors from state object with
 * @param errors - Object containing errors by {id}{field} keys
 * @param id - Id of resource
 * @param field - Field of resource to get errors for
 * @return [String] if there are errors, undefined if none are found
 */
const getPlatformErrors = (errors, id, field) => {
  let platformErrors;
  let errorObj = errors[id + field];
  if (!isNoU(errorObj)) {
    let errorKeys = [];
    if (errorObj.errorCode === "INVALID_VALUE") {
      for (let validationError of errorObj.failedValidations) {
        errorKeys.push({
          key: `errors.validation.${validationError.name}`,
          type: validationError.name,
          invalidValues: validationError.invalidValues
        });
      }
    } else {
      errorKeys.push({
        key: `errors.${errorObj.errorCode}`
      });
    }
    platformErrors = errorKeys;
  }
  return platformErrors;
};

// Temporary for sharing in withHandlers
// TODO: Remove and inline when only one of onPersistValue or onValueChanged exists
const valueChangeWrapper = (props) => (value, field, errors) => {
  props.onValueChanged(
    value,
    field,
    errors,
    props.fieldPath,
    props.resourcePath,
    props.sectionKeyPath,
    props.masterBreak,
    props.platformBreak
  );
};

const resolveResourceFieldPermissions = (permissions, resource, field) => {
    // No permissions restrictions if there is no resource
    if (isNoU(resource)) {
      return {allowedToRead: true, allowedToEdit: true};
    }

    // hallman hack
    if (isNoU(resource.resourceId)) {
        return {allowedToRead: true, allowedToEdit: true};
    }

    let allowedToRead = false;
    let allowedToEdit = false;

    let resourcePermissions = find(permissions, (permission) => permission.name === resource.resourceId.type.toLowerCase());

    if (!isNoU(resourcePermissions)) {
        let allFieldPermissions = find(resourcePermissions.resourceFieldPermissions, (fieldPermissions) => fieldPermissions.name === 'all');

        if (!isNoU(allFieldPermissions)) {
            allowedToEdit = includes(allFieldPermissions.fieldActions, "EDIT");
            allowedToRead = includes(allFieldPermissions.fieldActions, "READ");
        }

        let fieldPermissions = find(resourcePermissions.resourceFieldPermissions, (fieldPermissions) => fieldPermissions.name === field);

        if (!isNoU(fieldPermissions)) {
            allowedToEdit = includes(fieldPermissions.fieldActions, "EDIT");
            allowedToRead = includes(fieldPermissions.fieldActions, "READ");
        }
    }

    return {allowedToEdit, allowedToRead};
};

/**
 * Connects a component to Redux store and creates a new component.
 * Connected component will be auto registered for validation based on store state
 *
 * New component will expect the following props
 * {
 *    currentPlatform: "DAILYBREAK" | "EMBED" | "DEMO" | "MASTER" - Main platform being edited
 *    platform: "DAILYBREAK" | "EMBED" | "DEMO" | "MASTER" - Platform of this field
 *    reducerKey: String - Key used to access meta information in state
 *    sectionKeyPath: String[] - List of sections that the component is contained in
 *    resourcePath: String[] - List of properties leading to the resource the component is using
 *    fieldPath: String[] - List of properties leading the value the component is using
 *    field: String - Config object used to generate the component
 *    formProxy: ResourceFormProxy - Proxy to the form that generated the component
 *    fieldKey: String - Unique key for the component
 *    fieldLabel: Node! - Optional label for field
 * }
 *
 * Target component will received the following props
 * {
 *    defaultValue: Any - Value of this field
 *    linkedValue: Any - Value to use if this field is undefined
 *    isSaving: Boolean - Is this field currently being saved
 *    savedField: Boolean - Has this field been saved in current session
 *    errors: [String]! - List of error keys this field has
 *    linkedErrors: [String]! - List of error keys that linked field may have
 *    editable: Boolean - Is this field editable
 *    idAssetValue: Any - Object this field's value is a reference for
 *    required: Boolean - Does this field required a value
 *    showField: Boolean - Is this field currently showing
 *    disabled: Boolean - Is this field currently disabled
 *    fieldKey: String - Unique key for this field
 *    name: String - Name of this field
 *    onValidationSuccess: () => void - Trigger to say field is valid
 *    onValidationError: (name, rule, platform) => void - Trigger to say field is invalid
 *    onPersistValue: (value, field, errors) => void - This fields value has changed
 *    onValueChanged: (value, field, errors) => void - This fields value has changed
 * }
 *
 * The target component will also receive all values on the new components field.props object
 *
 * Additional props received by a preOmit HOC
 * {
 *    masterBreak: Break - Master version of Break object
 *    platformBreak: Break - Break object for this fields platform
 *    resourcePath: [String] - List of fields to resource of this field
 *    fieldPath: [String] - List of fields to value
 *    field: Object - Config for this field
 * }
 *
 * @param Target - Component to wrap
 * @param preOmit - An additional HOC to wrap target component in, will receive props detailed above
 * @param omitPaths
 * @return Node - New component
 */
const connectBreakField = (
  Target,
  /* additional HOC stages */
  {
    preOmit = null
  } = {},
  ...omitPaths
) => {
  const mapState = (state, ownProps) => {

    // References for later use
    const masterBreakId = state.editBreak.break.MASTER;
    const platformBreakId = state.editBreak.break[ownProps.platform];
    const masterBreak = state.resources.resources[masterBreakId];
    const platformBreak = state.resources.resources[platformBreakId];

    let name = ownProps.field ? ownProps.field.name : ownProps.name;
    let fieldKey = ownProps.fieldKey + ownProps.platform;

    let resource = getBreakResource(masterBreak, platformBreak, ownProps.resourcePath);
    let value = getBreakResource(masterBreak, platformBreak, ownProps.fieldPath);

    // Permissions
    let allPermissions = getUserResourcePermissions(state);
    let {allowedToRead, allowedToEdit} = resolveResourceFieldPermissions(allPermissions, resource, name);

      // Fill value with master's, if not master
    if (isUndefined(value) && ownProps.platform !== "MASTER") {
      value = getBreakResource(masterBreak, masterBreak, ownProps.fieldPath);
    }

    let isSaving = false;
    let savedField = false;
    let errors = null;

    // Some custom components don't require a resource, or the following fields, to work
    if (!isNoU(resource)) {
      isSaving = !isNoU(state.resources.pendingSaves[resource.id + name]);
      savedField = state.resources.savedField[resource.id + name];
      savedField = isNoU(savedField) ? false : savedField;
      errors = getPlatformErrors(state.resources.saveErrors, resource.id, name);
    }

    // Should this component be currently displaying
    let showField = true;
    if (ownProps.field) {
      if (isFunction(ownProps.field.showField)) {
        showField = isTruthy(ownProps.field.showField(ownProps.formProxy));
      }  
    } else {
      showField = ownProps.showField
    }

    // Is this field required in its resource's current state
    let required = isFunction(ownProps.field.required) ? ownProps.field.required(ownProps.formProxy) : ownProps.field.required;
    if (ownProps.field.requiredPlatforms && ownProps.platform !== "MASTER") {
      required = true;
    }

    // Disabled if is readonly field or this components platform is unsupported
    let disabled = ownProps.field.disabled ||  (resource === undefined) || false;
    if (ownProps.field.supportedPlatforms) {
      disabled = !includes(ownProps.field.supportedPlatforms, ownProps.platform);
    }

    // Is this component editable in its resource's current state
    let editable
    if (isFunction(ownProps.field.editable)) {
      editable = ownProps.field.editable(ownProps.formProxy);
    }

    const canCreate = !ownProps.field.canCreate || ownProps.field.canCreate(ownProps.formProxy);

    // Some fields are actually an id for another field object
    // id assets only supported on same object
    let idAsset = ownProps.field ? ownProps.field.idFor : null;
    let idAssetValue;
    if (idAsset) {
      // Get reference to related resource
      let idAssetFieldPath = ownProps.fieldPath.slice(0, ownProps.fieldPath.length - 1).concat(idAsset);
      idAssetValue = getBreakResource(masterBreak, platformBreak, idAssetFieldPath);

      // Get masters asset if platform's is undefined
      if (isUndefined(idAssetValue) && ownProps.platform !== "MASTER") {
        idAssetValue = getBreakResource(masterBreak, masterBreak, idAssetFieldPath);
      }
    }

    // Fields can inherit its value and errors from another field
    // Only one level currently supported
    let linkedValue;
    let linkedErrors;

    let linkedFieldPath = ownProps.field && ownProps.field.linkedField ? ownProps.field.linkedField(ownProps.formProxy) : null;
    if (linkedFieldPath) {
      linkedValue = getBreakResource(masterBreak, platformBreak, linkedFieldPath);

      // Get linked values Master reference if platform value is undefined
      if (isUndefined(linkedValue) && ownProps.platform !== "MASTER") {
        linkedValue = getBreakResource(masterBreak, masterBreak, linkedFieldPath);
      }

      let linkedLastField = linkedFieldPath[linkedFieldPath.length - 1];
      linkedErrors = getPlatformErrors(state.resources.saveErrors, platformBreak.resourceId.id, linkedLastField);

      if (linkedErrors !== null && !isEmpty(linkedErrors)) {
        // if one of these errors is present on a linked field but not this field, don't show that error on this field.
        linkedErrors.forEach(validationRule => {
          const isMatchingRule = error => error.type === validationRule;

          const [ownError, linkedError] = [errors, linkedErrors].map(errors => errors.find(isMatchingRule));

          if (linkedError && !ownError) {
            linkedErrors = reject(linkedErrors, isMatchingRule);
          }
        });
      }
    }

    return {
      // For merge
      masterBreak,
      platformBreak,

      // For field
      ...ownProps.field.props,
      defaultValue: value,
      linkedValue,
      isSaving,
      savedField,
      errors,
      linkedErrors,
      editable: allowedToEdit && editable,
      idAssetValue,
      required,
      showField: allowedToRead && showField,
      disabled,
      fieldKey,
      name,
      canCreate
    };
  };

  const mapDispatch = (dispatch, ownProps) => {
    return {
      onValueChanged: (value, platform, errors, fieldPath, resourcePath, sectionKeyPath, masterBreak, platformBreak) => {
        let platformBreakResourceId = platformBreak.resourceId;
        if (!platformBreakResourceId) {
          console.error("Root resource without resourceId", platformBreak);
          return;
        }

        // Set which resource was actually modified
        let modifiedResource = platformBreak;
        let modifiedResourceId = platformBreakResourceId;
        if (resourcePath.length > 0) {
          // If there is a resource path then the edited resource is at the end of that path
          // modifiedResource = dotProp.get(rootResource, resourcePath.join('.'));
          modifiedResource = getBreakResource(masterBreak, platformBreak, resourcePath);

          if (!modifiedResource) {
            console.error("Cannot identify resource");
            return;
          }

          if (!modifiedResource.resourceId) {
            console.error("Cannot save resource without resourceId 1", modifiedResource);
            return;
          }

          modifiedResourceId = modifiedResource.resourceId;
          if (!modifiedResourceId) {
            console.error("Cannot save resource without resourceId 2", modifiedResource);
            return;
          }
        }

        // Construct parent resource list
        let parentIds = extractResourceIdsWithPath(masterBreak, platformBreak, resourcePath);

        // Last field in path was the one edited on modifiedResource
        let lastFieldInPath = fieldPath[fieldPath.length - 1];

        dispatch(saveResourceFieldAction(
          modifiedResourceId,
          platformBreakResourceId,
          resourcePath,
          fieldPath,
          lastFieldInPath,
          value,
          parentIds,
          errors,
          sectionKeyPath
        ));
      },
      onValidationSuccess: () => {},
      onValidationError: (name, rule, platform, platformBreak) => {// Set which resource was actually modified
          let modifiedResource = platformBreak;
          let modifiedResourceId = platformBreak.resourceId;
          if (ownProps.resourcePath.length > 0) {
              // If there is a resource path then the edited resource is at the end of that path
              modifiedResource = dotProp.get(modifiedResource, ownProps.resourcePath.join('.'));
              if (!modifiedResource) {
                  console.error("Cannot raise error on null resource", modifiedResource, ownProps.resourcePath);
                  return;
              }
          }

          if (!modifiedResourceId) {
              console.error("Cannot raise error on null resourceId", modifiedResource);
              return;
          }

          dispatch(saveResourceFieldErrorAction(modifiedResourceId.id, name, null, {
              errorCode: "INVALID_VALUE",
              failedValidations: rule.map(item => ({name: item.type})),
              sectionKeyPath: ownProps.sectionKeyPath
          }, modifiedResourceId, ownProps.fieldPath));
      }
    };
  };

  // Simple component to hide the Target component based on pre-calculated 'showField' prop
  const Hider = (props) => props.showField ? <Target {...props}/> : null;

  const HOCs = [
    catchErrors,
    // Components wrapped with BreakField should only be updating from Redux below
    // So we block any updates that can be caused by a parent component
    blockParentUpdates,
    connect(mapState, mapDispatch),
    // Make static reference to method injecting props
    withHandlers({
      onValueChanged: valueChangeWrapper,
      onPersistValue: valueChangeWrapper,
      onValidationSuccess: (props) => () => props.onValidationSuccess(),
      onValidationError: (props) => (name, rule, platform) => props.onValidationError(name, rule, platform, props.platformBreak)
    }),
  ];

  if (preOmit) {
    HOCs.push(preOmit);
  }

  HOCs.push(
    // Need for omitting functionality
    // withProps and mapProps from recompose seem to deep clone in some way that's causing a render with no changes

    // Want final props to be as flat as possible
    // masterBreak and platformBreak are needed by the above withHandlers
    // but we don't want them in the Target component
    // TODO: Gotta be a better way than another connect, maybe a simple custom HOC
    connect(() => ({}), () => ({}), (state, dispatch, ownProps) => {
      return omit(ownProps,
        // Currently we are deep cloning state
        // so if these are passed down a re-render will always happen
        "masterBreak",
        "platformBreak",

        // Unnecessary to pass through
        "fieldPath",
        "resourcePath",
        "field",

        // Caller specified omissions
        ...omitPaths
      );
    })
  );

  const BreakFieldComposed = compose(...HOCs);

  return BreakFieldComposed(Hider);
};

export default connectBreakField;



// WEBPACK FOOTER //
// ./public/hoc/BreakField.jsx